import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { useApiClient } from "../../api/ApiProvider";
import { ApiError, isPaymentRequired } from "../../api/errors";
import {
  queryKeys,
  useCreateEnrollToken,
  useCreateEnvironment,
  useCreateInstance,
  useEnvironments,
  useHost,
  useNodes,
  useProviderCatalog,
  useProviderCredentials,
} from "../../api/queries";
import type { ComposeImportResult } from "../../api/client";
import type { Node } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import { canAdministerFleet, canSeeBilling } from "../../auth/roleView";
import { Spinner } from "../../components/AsyncStates";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { CopyButton } from "../../components/CopyButton";
import { Modal } from "../../components/Modal";
import { StatePill } from "../../components/StatePill";
import { useToast } from "../../toast/ToastContext";
import { PROVIDER_LABEL } from "../provider-credentials/ProviderCredentialsPage";
import { RECIPES, findRecipe } from "../gallery/catalog";
import { findRoutedService } from "./composePorts";
import { isValidHostname, urlForHostname } from "./hostname";
import "../../components/Form.css";
import "../nodes/add-host.css";
import "./deploy-wizard.css";

// ---- Types ----

/**
 * The wizard step pipeline. Steps are ordered — the wizard walks forward
 * through them and can step back. Future steps (corpus seeding) slot in by
 * adding to this union and inserting their panel in the render switch.
 *
 * "laptop" is the Founder zero-server on-ramp (journey.md rung 3, G13):
 * mints a `local` enroll token and waits for the laptop to connect, then
 * returns to "target" with it selected. "hostname" sits between "target"
 * and "secrets"/"review" (journey.md rung 4, G8): the product only routes
 * through bound domains, so the wizard finishes that sentence before
 * deploying rather than leaving the service unreachable.
 */
type WizardStep = "source" | "target" | "provision" | "laptop" | "hostname" | "secrets" | "review" | "deploying";

interface SecretEntry {
  name: string;
  note: string;
  value: string;
}

interface ServiceDeployStatus {
  serviceId: string;
  serviceName: string;
  status: "pending" | "placing" | "deploying" | "done" | "error";
  error: string | null;
}

/**
 * The deploy execution's progress log — append-only during the run.
 * Each entry is a discrete phase (create project, import, secrets,
 * per-service placement+deploy). The `done` flag flips when the phase
 * completes; `error` captures a failure message.
 */
interface ProgressEntry {
  label: string;
  detail: string;
  done: boolean;
  error: string | null;
}

// ---- Props ----

export interface DeployWizardProps {
  open: boolean;
  onClose: () => void;
  /** Pre-selected recipe id (from Gallery's "Deploy this stack"). */
  initialRecipeId?: string;
  /** Pre-filled compose text (from a caller that already has compose YAML). */
  initialCompose?: string;
}

// ---- Helpers ----

const SECRET_REF_RE = /\$\{secret:([^}]+)\}/g;

function extractSecretNames(compose: string): string[] {
  const names = new Set<string>();
  let match: RegExpExecArray | null;
  SECRET_REF_RE.lastIndex = 0;
  while ((match = SECRET_REF_RE.exec(compose)) !== null) {
    names.add(match[1]);
  }
  return Array.from(names);
}

function nodeDisplayName(node: Node): string {
  return node.name || node.hostname || node.id;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 63);
}

/** Generates a client-side agent id to pre-assign on a "use this laptop"
 * enroll token (PR #634 security audit condition 5, A-5) — mirrors the
 * cloud-provisioning pattern of reserving the identity before the agent
 * exists. Not a credential and never treated as one (S-13 doesn't apply
 * here); just an opaque, believably-unique node identifier. */
function generateLaptopAgentId(): string {
  return `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function stepTitle(step: WizardStep): string {
  switch (step) {
    case "source": return "Deploy a stack";
    case "target": return "Choose a target";
    case "provision": return "Provision a node";
    case "laptop": return "Use this laptop";
    case "hostname": return "Bind a hostname";
    case "secrets": return "Configure secrets";
    case "review": return "Review and deploy";
    case "deploying": return "Deploying…";
  }
}

function locationLabel(location: { name: string; city: string }): string {
  return `${location.city} (${location.name})`;
}

function priceForLocation(
  serverType: { prices: Array<{ location: string; priceMonthlyGross: string }> },
  location: string,
): string | undefined {
  return serverType.prices.find((p) => p.location === location)?.priceMonthlyGross;
}

function serverTypeLabel(
  serverType: { name: string; cores: number; memoryGB: number; diskGB: number },
  price: string | undefined,
): string {
  const specs = `${serverType.cores} vCPU, ${serverType.memoryGB} GB RAM, ${serverType.diskGB} GB SSD`;
  const priceLabel = price ? `€${Number(price).toFixed(2)}/mo` : "price unavailable in this region";
  return `${serverType.name.toUpperCase()} — ${specs} — ${priceLabel}`;
}

// ---- Component ----

export function DeployWizard({
  open,
  onClose,
  initialRecipeId,
  initialCompose,
}: DeployWizardProps) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { pushToast } = useToast();
  const [, navigate] = useLocation();

  // ---- Step state ----
  const [step, setStep] = useState<WizardStep>("source");

  // ---- Source step ----
  const [sourceMode, setSourceMode] = useState<"recipe" | "compose">(
    initialCompose ? "compose" : "recipe",
  );
  const [selectedRecipeId, setSelectedRecipeId] = useState(initialRecipeId ?? "");
  const [composeText, setComposeText] = useState(initialCompose ?? "");
  const [projectName, setProjectName] = useState(() => {
    if (initialRecipeId) {
      const r = findRecipe(initialRecipeId);
      return r ? slugify(r.name) : "";
    }
    return "";
  });

  // ---- Derived from source (moved up: the laptop/hostname steps below need
  // effectiveCompose before the deploy step's own derived block did) ----
  const selectedRecipe = selectedRecipeId ? findRecipe(selectedRecipeId) : undefined;
  const effectiveCompose =
    sourceMode === "recipe" && selectedRecipe ? selectedRecipe.compose : composeText;
  const secretNames = extractSecretNames(effectiveCompose);
  const needsSecrets = secretNames.length > 0;
  // The service compose's own `ports:` would have exposed, IF Comebye
  // published host ports the way plain Docker does — it doesn't (edge-routed
  // model, ADR-0026); this is what the hostname step offers to bind instead.
  const routedService = useMemo(() => findRoutedService(effectiveCompose), [effectiveCompose]);

  // ---- Target step ----
  const [selectedNodeId, setSelectedNodeId] = useState("");
  // Polling is an explicit, documented exception to P-1 (event-driven, not
  // polled): while waiting on the laptop step, the only "event" is a human
  // running a shell command on a machine the browser has no other way to
  // observe. Every other step leaves this off.
  const { data: nodesPage } = useNodes({ limit: 100 }, { refetchInterval: step === "laptop" ? 2000 : false });
  const availableNodes = (nodesPage?.items ?? []).filter(
    (n) => n.status === "online",
  );

  // ---- Provision step ----
  const [providerCredId, setProviderCredId] = useState("");
  const [provisionRegion, setProvisionRegion] = useState("");
  const [provisionServerType, setProvisionServerType] = useState("");
  const [provisioningAgentId, setProvisioningAgentId] = useState<string | null>(null);
  const createInstanceMutation = useCreateInstance();
  const { data: credentialsPage, isPending: credentialsPending } = useProviderCredentials();
  const {
    data: providerCatalog,
    isPending: catalogPending,
    isError: catalogIsError,
  } = useProviderCatalog(providerCredId || undefined);

  const credentials = credentialsPage?.items ?? [];
  const catalogLocations = providerCatalog?.locations ?? [];
  const catalogServerTypes = useMemo(() => {
    const available = providerCatalog?.serverTypes ?? [];
    return available
      .filter((s) => priceForLocation(s, provisionRegion) !== undefined)
      .sort((a, b) => {
        const priceA = Number(priceForLocation(a, provisionRegion)!);
        const priceB = Number(priceForLocation(b, provisionRegion)!);
        return priceA - priceB;
      });
  }, [providerCatalog, provisionRegion]);

  useEffect(() => {
    if (catalogLocations.length === 0) { setProvisionRegion(""); return; }
    if (!catalogLocations.some((l) => l.name === provisionRegion)) {
      setProvisionRegion(catalogLocations[0].name);
    }
  }, [catalogLocations, provisionRegion]);

  useEffect(() => {
    if (catalogServerTypes.length === 0) { setProvisionServerType(""); return; }
    if (!catalogServerTypes.some((s) => s.name === provisionServerType)) {
      setProvisionServerType(catalogServerTypes[0].name);
    }
  }, [catalogServerTypes, provisionServerType]);

  const { data: provisioningHost } = useHost(provisioningAgentId ?? undefined);

  const catalogLoaded = providerCredId.length > 0 && !catalogPending && !catalogIsError;
  const canProvision =
    providerCredId.length > 0 &&
    provisionRegion.length > 0 &&
    provisionServerType.length > 0 &&
    catalogLoaded &&
    !createInstanceMutation.isPending &&
    !provisioningAgentId;
  const paymentRequired = createInstanceMutation.isError && isPaymentRequired(createInstanceMutation.error);

  // ---- Laptop step ("Use this laptop", journey.md rung 3 / G13) ----
  // The Founder's zero-server on-ramp: mint a `local` enroll token scoped to
  // a development-class environment (auto-picked when there's exactly one,
  // an explicit choice when there's more — never production-class, ADR-0034)
  // and wait for the laptop to connect, exactly what `comebye local` does
  // from the CLI — this is the web affordance the CLI-only path was missing.
  const { session } = useAuth();
  const canViewBilling = canSeeBilling(session?.role);
  // node:create / environment:create are admin-only server-side (PR #634
  // security audit condition 1) — canManageFleet (which the pages that open
  // this wizard gate on) also passes `operator`, so "Use this laptop" and
  // "Create a dev environment" need their own, stricter check or an
  // operator's primary empty-state button would only ever 403.
  const canAdminister = canAdministerFleet(session?.role);
  const { data: environmentsPage } = useEnvironments({ limit: 100 }, { enabled: open });
  const environments = environmentsPage?.items ?? [];
  // Only development-class environments are ever offered here — a `local`
  // node can never join a production-class one (ADR-0034), so
  // production/staging never appear as a choice, not even implicitly.
  const devEnvironments = environments.filter((e) => e.class === "development");
  const devEnvFieldId = useId();
  const [selectedDevEnvName, setSelectedDevEnvName] = useState("");
  // Auto-uses the one dev-class environment when there's exactly one (no
  // meaningful choice to make); with more than one, the operator must name
  // one explicitly (PR #634 security audit condition 2) — this used to
  // silently pick devEnvironments[0], which could mint against the wrong
  // one the instant the list loaded.
  const devEnvironment =
    devEnvironments.length === 1 ? devEnvironments[0] : devEnvironments.find((e) => e.name === selectedDevEnvName);
  const createEnvironmentMutation = useCreateEnvironment();
  const mintLaptopToken = useCreateEnrollToken();
  // The node ids visible the moment "Use this laptop" was clicked — used
  // only as the FALLBACK match (a server that omits `agentId` in its
  // response, e.g. mid version-skew) so the poll still waits for a NEW
  // local+online id outside this set rather than an already-existing local
  // node (e.g. a homelab machine enrolled earlier).
  const [preLaptopNodeIds, setPreLaptopNodeIds] = useState<Set<string> | null>(null);
  // Generated client-side and sent as `agentId` on the mint call (PR #634
  // security audit condition 5, A-5): pre-assigns the enrolling node's
  // identity the same way cloud provisioning does (`Instance.agentId`,
  // "reserved BEFORE the host exists"), so the poll below can select
  // EXACTLY that node once online — never "any new local+online node",
  // which a second laptop enrolling around the same time would confuse.
  const [laptopAgentId, setLaptopAgentId] = useState<string | null>(null);
  // Incremented each time minting should (re-)start: on entering the step,
  // on "Try again", and implicitly whenever `devEnvironment` newly resolves
  // (e.g. right after "Create a dev environment" succeeds, or the operator
  // picks one among several).
  const [laptopMintAttempt, setLaptopMintAttempt] = useState(0);
  const laptopEnvPaymentRequired =
    createEnvironmentMutation.isError && isPaymentRequired(createEnvironmentMutation.error);

  useEffect(() => {
    if (step !== "laptop" || laptopMintAttempt === 0 || !devEnvironment) return;
    mintLaptopToken.mutate({
      environment: devEnvironment.name,
      nodeType: "local",
      agentId: laptopAgentId ?? undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, laptopMintAttempt, devEnvironment?.id]);

  useEffect(() => {
    if (step !== "laptop" || !preLaptopNodeIds || !mintLaptopToken.data) return;
    const items = nodesPage?.items ?? [];
    const respondedAgentId = mintLaptopToken.data.agentId;
    // Exact match on the pre-assigned agentId when the server returned one;
    // the "any new local+online node" heuristic is a fallback only, never
    // the primary path.
    const found = respondedAgentId
      ? items.find((n) => n.id === respondedAgentId && n.status === "online")
      : items.find((n) => n.nodeType === "local" && n.status === "online" && !preLaptopNodeIds.has(n.id));
    if (found) {
      setSelectedNodeId(found.id);
      setPreLaptopNodeIds(null);
      setLaptopAgentId(null);
      setLaptopMintAttempt(0);
      mintLaptopToken.reset();
      setStep("target");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, preLaptopNodeIds, mintLaptopToken.data?.token, mintLaptopToken.data?.agentId, nodesPage]);

  function goToLaptop() {
    setPreLaptopNodeIds(new Set((nodesPage?.items ?? []).map((n) => n.id)));
    setSelectedDevEnvName("");
    setLaptopAgentId(generateLaptopAgentId());
    setLaptopMintAttempt((n) => n + 1);
    setStep("laptop");
  }

  function retryLaptopMint() {
    setLaptopAgentId(generateLaptopAgentId());
    setLaptopMintAttempt((n) => n + 1);
  }

  // Leaving the laptop step (Back) drops the minted token immediately
  // (PR #634 security audit condition 6, S-13) — it never lingers in
  // memory once the operator isn't looking at it, same discipline as
  // closing the wizard entirely.
  function leaveLaptopStep() {
    mintLaptopToken.reset();
    setPreLaptopNodeIds(null);
    setLaptopAgentId(null);
    setLaptopMintAttempt(0);
    setStep("target");
  }

  const laptopToken = mintLaptopToken.data?.token;
  const laptopCommand = laptopToken
    ? `COMEBYE_JOIN_TOKEN=${laptopToken} kelpie -local -server ${window.location.origin} -tunnel ${window.location.hostname}:8444`
    : "";

  // ---- Hostname step (journey.md rung 4 / G8) ----
  // Bound BEFORE the deploy runs, so that whatever makes a bound hostname
  // route on a Docker Desktop laptop (PR #633, journey.md G12 — NOT landed
  // as of this PR, tracked separately) can see the domain already exists at
  // deploy time — the wizard finishes the sentence the service page's
  // Domains section already starts ("bind a hostname to reach this
  // service") instead of leaving the done-state unreachable.
  const hostnameId = useId();
  const [hostname, setHostname] = useState("");
  const [hostnameSkipped, setHostnameSkipped] = useState(false);

  function prepareHostname(isLocalTarget: boolean) {
    setHostname(isLocalTarget ? `${slugify(projectName)}.localhost` : "");
    setHostnameSkipped(false);
  }

  // ---- Secrets step ----
  const [secrets, setSecrets] = useState<SecretEntry[]>([]);

  // ---- Deploy step ----
  // Use refs for the progress log to avoid stale-closure issues in the
  // async deploy chain. The state copy drives re-renders.
  const progressRef = useRef<ProgressEntry[]>([]);
  const [progressSnapshot, setProgressSnapshot] = useState<ProgressEntry[]>([]);
  const [serviceStatuses, setServiceStatuses] = useState<ServiceDeployStatus[]>([]);
  const [deployDone, setDeployDone] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);
  // "skipped" (default/no routed service/explicitly skipped), "bound" (the
  // domain call succeeded), or "failed" (attempted, refused) — drives the
  // done-state's URL-or-honest-line choice below (A-10).
  const [domainBindResult, setDomainBindResult] = useState<"skipped" | "bound" | "failed">("skipped");

  // ---- IDs ----
  const nameId = useId();
  const composeId = useId();
  const credentialFieldId = useId();
  const regionFieldId = useId();
  const serverTypeFieldId = useId();

  // Auto-advance from provision step when host reaches "online" — a
  // provisioned cloud node is never `local`, so the hostname step gets no
  // `.localhost` default for it.
  useEffect(() => {
    if (provisioningHost?.state === "online" && provisioningAgentId && step === "provision") {
      setSelectedNodeId(provisioningAgentId);
      setProvisioningAgentId(null);
      prepareHostname(false);
      setStep("hostname");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provisioningHost?.state, provisioningAgentId, step]);

  // ---- Reset ----
  function resetState() {
    setStep("source");
    setSourceMode(initialCompose ? "compose" : "recipe");
    setSelectedRecipeId(initialRecipeId ?? "");
    setComposeText(initialCompose ?? "");
    setProjectName(() => {
      if (initialRecipeId) {
        const r = findRecipe(initialRecipeId);
        return r ? slugify(r.name) : "";
      }
      return "";
    });
    setSelectedNodeId("");
    setProviderCredId("");
    setProvisionRegion("");
    setProvisionServerType("");
    setProvisioningAgentId(null);
    createInstanceMutation.reset();
    setPreLaptopNodeIds(null);
    setSelectedDevEnvName("");
    setLaptopAgentId(null);
    setLaptopMintAttempt(0);
    mintLaptopToken.reset();
    createEnvironmentMutation.reset();
    setHostname("");
    setHostnameSkipped(false);
    setSecrets([]);
    progressRef.current = [];
    setProgressSnapshot([]);
    setServiceStatuses([]);
    setDeployDone(false);
    setDeployError(null);
    setCreatedProjectId(null);
    setDomainBindResult("skipped");
  }

  function handleClose() {
    if (step === "deploying" && !deployDone) return;
    if (step === "provision" && provisioningAgentId) return;
    resetState();
    onClose();
  }

  // ---- Auto-fill project name from recipe ----
  function handleRecipeChange(recipeId: string) {
    setSelectedRecipeId(recipeId);
    const recipe = findRecipe(recipeId);
    if (recipe) {
      setProjectName(slugify(recipe.name));
    }
  }

  // ---- Navigation helpers ----
  function prepareSecrets() {
    if (sourceMode === "recipe" && selectedRecipe?.secrets) {
      setSecrets(
        selectedRecipe.secrets.map((s) => ({ name: s.name, note: s.note, value: "" })),
      );
    } else {
      const names = extractSecretNames(effectiveCompose);
      setSecrets(names.map((name) => ({ name, note: "", value: "" })));
    }
  }

  function goToTarget() {
    prepareSecrets();
    setStep("target");
  }

  function goFromTarget() {
    const node = availableNodes.find((n) => n.id === selectedNodeId);
    prepareHostname(node?.nodeType === "local");
    setStep("hostname");
  }

  function goFromHostname() {
    if (needsSecrets) {
      setStep("secrets");
    } else {
      setStep("review");
    }
  }

  // ---- Provision submit ----
  function handleProvisionSubmit() {
    createInstanceMutation.mutate(
      { providerCredId, region: provisionRegion, serverType: provisionServerType },
      {
        onSuccess: (instance) => {
          if (instance.agentId) {
            setProvisioningAgentId(instance.agentId);
          }
        },
      },
    );
  }

  // ---- Progress helpers (ref-based to avoid stale closures) ----
  function appendProgress(label: string, detail: string): number {
    const entry: ProgressEntry = { label, detail, done: false, error: null };
    progressRef.current = [...progressRef.current, entry];
    setProgressSnapshot([...progressRef.current]);
    return progressRef.current.length - 1;
  }

  function markProgressDone(index: number, error?: string) {
    progressRef.current = progressRef.current.map((p, i) =>
      i === index ? { ...p, done: true, error: error ?? null } : p,
    );
    setProgressSnapshot([...progressRef.current]);
  }

  function updateSecretValue(name: string, value: string) {
    setSecrets((prev) =>
      prev.map((s) => (s.name === name ? { ...s, value } : s)),
    );
  }

  // ---- The deploy chain ----
  async function executeDeploy() {
    setStep("deploying");
    setDeployDone(false);
    setDeployError(null);
    setDomainBindResult("skipped");
    progressRef.current = [];
    setProgressSnapshot([]);
    setServiceStatuses([]);

    try {
      // 1. Create project — on name conflict, auto-append a numeric suffix
      //    rather than forcing the user back to the source step.
      const idx0 = appendProgress("Creating project", projectName.trim());
      let resolvedName = projectName.trim();
      let project: Awaited<ReturnType<typeof client.createProject>>;
      try {
        project = await client.createProject({ name: resolvedName });
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          resolvedName = `${resolvedName}-${Date.now() % 10000}`;
          project = await client.createProject({ name: resolvedName });
        } else {
          throw err;
        }
      }
      setCreatedProjectId(project.id);
      markProgressDone(idx0);

      // 2. Import compose
      const idx1 = appendProgress("Importing compose", "services and connections");
      const importResult: ComposeImportResult = await client.importCompose(
        project.id,
        effectiveCompose,
      );
      markProgressDone(idx1);

      // 2.5. Bind the hostname chosen on the hostname step, if any — BEFORE
      //      any service deploys, so the routed service's domain exists at
      //      deploy time. PR #633 (journey.md G12, not landed as of this PR)
      //      is what makes a `*.localhost` hostname actually route on a
      //      Docker Desktop laptop, and needs the domain to exist by then —
      //      binding first is what lets it see the port. A refusal here is
      //      recorded but never aborts the deploy (charter §1 corollary: a
      //      control-plane/deploy-adjacent failure never stops the rest of
      //      the run) — the operator can always bind a hostname later from
      //      the service page.
      const trimmedHostname = hostname.trim();
      if (routedService && !hostnameSkipped && trimmedHostname.length > 0) {
        const routedImportedService = importResult.services.find(
          (s) => s.name === routedService.serviceName,
        );
        const idxDomain = appendProgress("Binding hostname", trimmedHostname);
        if (!routedImportedService) {
          setDomainBindResult("failed");
          markProgressDone(idxDomain, `Service "${routedService.serviceName}" was not found after import.`);
        } else {
          try {
            await client.createDomain(project.id, routedImportedService.id, {
              hostname: trimmedHostname,
              targetPort: routedService.containerPort,
            });
            setDomainBindResult("bound");
            markProgressDone(idxDomain);
          } catch (err) {
            const msg = err instanceof ApiError ? err.message : "Couldn't bind that hostname.";
            setDomainBindResult("failed");
            markProgressDone(idxDomain, msg);
          }
        }
      }

      // 3. Create secrets (if any)
      const secretsToCreate = secrets.filter((s) => s.value.trim().length > 0);
      if (secretsToCreate.length > 0) {
        const idx2 = appendProgress(
          "Creating secrets",
          `${secretsToCreate.length} secret${secretsToCreate.length === 1 ? "" : "s"}`,
        );
        for (const secret of secretsToCreate) {
          await client.createSecret({ name: secret.name, value: secret.value });
        }
        markProgressDone(idx2);
      }

      // 4. Place + deploy each service
      const services = importResult.services;
      const initialStatuses: ServiceDeployStatus[] = services.map((svc) => ({
        serviceId: svc.id,
        serviceName: svc.name,
        status: "pending" as const,
        error: null,
      }));
      setServiceStatuses(initialStatuses);

      for (const svc of services) {
        setServiceStatuses((prev) =>
          prev.map((s) =>
            s.serviceId === svc.id ? { ...s, status: "placing" } : s,
          ),
        );

        try {
          await client.createPlacement(project.id, svc.id, {
            nodeId: selectedNodeId,
          });

          setServiceStatuses((prev) =>
            prev.map((s) =>
              s.serviceId === svc.id ? { ...s, status: "deploying" } : s,
            ),
          );

          await client.deployService(project.id, svc.id);

          setServiceStatuses((prev) =>
            prev.map((s) =>
              s.serviceId === svc.id ? { ...s, status: "done" } : s,
            ),
          );
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : "Deploy failed";
          setServiceStatuses((prev) =>
            prev.map((s) =>
              s.serviceId === svc.id ? { ...s, status: "error", error: msg } : s,
            ),
          );
        }
      }

      setDeployDone(true);
      const targetNode = availableNodes.find((n) => n.id === selectedNodeId);
      pushToast({
        title: `Deployed ${services.length} service${services.length === 1 ? "" : "s"}${targetNode ? ` to ${nodeDisplayName(targetNode)}` : ""}.`,
        tone: "success",
      });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Something went wrong";
      setDeployError(msg);
      setDeployDone(true);
    }
  }

  // ---- Validation ----
  const sourceValid =
    projectName.trim().length > 0 &&
    (sourceMode === "recipe"
      ? selectedRecipeId !== ""
      : composeText.trim().length > 0);
  const targetValid = selectedNodeId !== "";
  const hostnameValid =
    !routedService || hostnameSkipped || (hostname.trim().length > 0 && isValidHostname(hostname.trim()));
  const secretsValid = secrets.every((s) => s.value.trim().length > 0);

  // ---- Done-state URL (journey.md rung 4 / G8) ----
  // Only claims the app is live once EVERY service finished ("done") — never
  // while something is still mid-deploy or errored — and otherwise says so
  // honestly rather than rendering an inert/lying link (A-10).
  const allServicesDone = serviceStatuses.length > 0 && serviceStatuses.every((s) => s.status === "done");
  const routedServiceStatus = routedService
    ? serviceStatuses.find((s) => s.serviceName === routedService.serviceName)
    : undefined;
  // Trimmed consistently with every other read of `hostname` (the value
  // actually sent to `createDomain` in executeDeploy is `hostname.trim()`)
  // — an untrimmed read here would show a URL that doesn't match what was
  // actually bound (PR #634 security audit condition 6).
  const trimmedHostnameForUrl = hostname.trim();
  const doneUrl: { kind: "live"; url: string } | { kind: "routed-service-failed"; serviceName: string } | { kind: "none" } =
    domainBindResult === "bound" && trimmedHostnameForUrl
      ? allServicesDone
        ? { kind: "live", url: urlForHostname(trimmedHostnameForUrl) }
        : routedServiceStatus?.status === "error"
          ? { kind: "routed-service-failed", serviceName: routedServiceStatus.serviceName }
          : { kind: "none" }
      : { kind: "none" };

  // ---- Render ----
  return (
    <Modal open={open} title={stepTitle(step)} onClose={handleClose} size="lg">
      {step === "source" && (
        <div className="wizard-step">
          <div className="wizard-step__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={sourceMode === "recipe"}
              className={`wizard-tab ${sourceMode === "recipe" ? "wizard-tab--active" : ""}`}
              onClick={() => setSourceMode("recipe")}
            >
              Pick a scaffold
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={sourceMode === "compose"}
              className={`wizard-tab ${sourceMode === "compose" ? "wizard-tab--active" : ""}`}
              onClick={() => setSourceMode("compose")}
            >
              Paste compose
            </button>
          </div>

          {sourceMode === "recipe" && (
            <div className="wizard-recipes" role="radiogroup" aria-label="Stack recipes">
              {RECIPES.map((recipe) => (
                <label
                  key={recipe.id}
                  className={`wizard-recipe ${selectedRecipeId === recipe.id ? "wizard-recipe--selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="recipe"
                    value={recipe.id}
                    checked={selectedRecipeId === recipe.id}
                    onChange={() => handleRecipeChange(recipe.id)}
                    className="visually-hidden"
                  />
                  <span className="wizard-recipe__name">{recipe.name}</span>
                  <span className="wizard-recipe__desc">{recipe.description}</span>
                </label>
              ))}
            </div>
          )}

          {sourceMode === "compose" && (
            <label className="form-field" htmlFor={composeId}>
              <span>compose.yml</span>
              <textarea
                id={composeId}
                spellCheck={false}
                rows={12}
                placeholder={"services:\n  web:\n    image: nginx:1.27\n    ports:\n      - \"80:80\"\n"}
                value={composeText}
                onChange={(e) => setComposeText(e.target.value)}
              />
            </label>
          )}

          <label className="form-field" htmlFor={nameId}>
            <span>Project name</span>
            <input
              id={nameId}
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              maxLength={63}
              placeholder="my-app"
            />
          </label>

          <div className="form-actions">
            <Button variant="secondary" onClick={handleClose}>Cancel</Button>
            <Button variant="primary" disabled={!sourceValid} onClick={goToTarget}>
              Next: pick a target
            </Button>
          </div>
        </div>
      )}

      {step === "target" && (
        <div className="wizard-step">
          <p className="wizard-step__intro">
            Where should <strong>{projectName}</strong> run?
          </p>

          {availableNodes.length === 0 ? (
            <div className="wizard-empty">
              <p>No online nodes found.</p>
              <p className="wizard-empty__hint">
                {canAdminister
                  ? "Use your own laptop, provision a cloud node, or enroll an existing machine (install Kelpie on any server or run Bonnie locally)."
                  : "Provision a cloud node, or ask an admin to enroll a machine (a laptop or any server)."}
              </p>
              <div className="wizard-empty__action wizard-empty__action--stacked">
                {canAdminister ? (
                  <Button variant="primary" onClick={goToLaptop}>
                    Use this laptop
                  </Button>
                ) : (
                  <p className="wizard-empty__hint">Ask an admin to enrol a laptop.</p>
                )}
                <Button variant="secondary" onClick={() => setStep("provision")}>
                  Provision a new node
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="wizard-nodes" role="radiogroup" aria-label="Target nodes">
                {availableNodes.map((node) => (
                  <label
                    key={node.id}
                    className={`wizard-node ${selectedNodeId === node.id ? "wizard-node--selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="node"
                      value={node.id}
                      checked={selectedNodeId === node.id}
                      onChange={() => setSelectedNodeId(node.id)}
                      className="visually-hidden"
                    />
                    <span className="wizard-node__name">
                      {nodeDisplayName(node)}
                    </span>
                    <span className="wizard-node__meta">
                      {node.hostname}
                      {node.containerCount > 0 &&
                        ` · ${node.containerCount} container${node.containerCount === 1 ? "" : "s"}`}
                    </span>
                  </label>
                ))}
              </div>

              <div className="wizard-laptop-card">
                <div>
                  <p className="wizard-laptop-card__title">Don't have a server yet?</p>
                  <p className="wizard-laptop-card__hint">
                    {canAdminister
                      ? "Enroll this machine as a local development node — needs Docker running."
                      : "Ask an admin to enrol a laptop as a local development node."}
                  </p>
                </div>
                {canAdminister && (
                  <Button variant="secondary" onClick={goToLaptop}>
                    Use this laptop
                  </Button>
                )}
              </div>
            </>
          )}

          <div className="wizard-provision-link">
            <button type="button" className="wizard-text-btn" onClick={() => setStep("provision")}>
              Or provision a new cloud node
            </button>
          </div>

          <div className="form-actions">
            <Button variant="secondary" onClick={() => setStep("source")}>Back</Button>
            <Button variant="primary" disabled={!targetValid} onClick={goFromTarget}>
              Next: bind a hostname
            </Button>
          </div>
        </div>
      )}

      {step === "provision" && (
        <div className="wizard-step">
          {provisioningAgentId ? (
            provisioningHost?.state === "provision_failed" ? (
              <div className="wizard-provisioning">
                <Banner tone="danger" title="Provisioning failed">
                  The cloud provider couldn't create your node. Check your
                  credential and provider dashboard, then try again.
                </Banner>
                <div className="form-actions">
                  <Button variant="secondary" onClick={() => setStep("target")}>Back to targets</Button>
                  <Button variant="primary" onClick={() => {
                    setProvisioningAgentId(null);
                    createInstanceMutation.reset();
                  }}>
                    Try again
                  </Button>
                </div>
              </div>
            ) : (
              <div className="wizard-provisioning">
                <p className="wizard-provisioning__status">
                  <Spinner />
                  {provisioningHost?.state === "provisioning"
                    ? "Booting your node… this can take a minute."
                    : provisioningHost?.state === "enrolling"
                      ? "Node is up — waiting for its agent to check in."
                      : "Starting provisioning…"}
                </p>
                <div className="wizard-provisioning__dots" aria-hidden="true">
                  <span className={`wizard-provisioning__dot ${
                    provisioningHost?.state === "provisioning" ? "wizard-provisioning__dot--active"
                    : provisioningHost?.state === "enrolling" || provisioningHost?.state === "online"
                      ? "wizard-provisioning__dot--done" : "wizard-provisioning__dot--pending"
                  }`} />
                  <span className="wizard-provisioning__dot-line" />
                  <span className={`wizard-provisioning__dot ${
                    provisioningHost?.state === "enrolling" ? "wizard-provisioning__dot--active"
                    : provisioningHost?.state === "online"
                      ? "wizard-provisioning__dot--done" : "wizard-provisioning__dot--pending"
                  }`} />
                </div>
                <dl className="wizard-provisioning__detail">
                  <div className="wizard-summary__row">
                    <dt>Provisioning</dt>
                    <dd>{provisioningHost?.state === "provisioning" ? "In progress" : "Done"}</dd>
                  </div>
                  <div className="wizard-summary__row">
                    <dt>Enrolling</dt>
                    <dd>{provisioningHost?.state === "enrolling" ? "In progress"
                      : provisioningHost?.state === "online" ? "Done" : "Waiting"}</dd>
                  </div>
                </dl>
              </div>
            )
          ) : (
            <>
              {createInstanceMutation.isError && !paymentRequired && (
                <p className="form-error" role="alert">
                  {createInstanceMutation.error instanceof Error
                    ? createInstanceMutation.error.message
                    : "Couldn't provision this instance."}
                </p>
              )}

              {paymentRequired && createInstanceMutation.error && (
                <Banner tone="warning" title="Licensed capacity reached">
                  {createInstanceMutation.error.message}
                </Banner>
              )}

              <label className="form-field" htmlFor={credentialFieldId}>
                <span>Provider credential</span>
                <select
                  id={credentialFieldId}
                  value={providerCredId}
                  onChange={(e) => {
                    setProviderCredId(e.target.value);
                    setProvisionRegion("");
                    setProvisionServerType("");
                  }}
                  required
                  disabled={credentialsPending}
                >
                  <option value="" disabled>
                    {credentialsPending ? "Loading credentials…" : "Select a credential"}
                  </option>
                  {credentials.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label} ({PROVIDER_LABEL[c.kind]})
                    </option>
                  ))}
                </select>
                {!credentialsPending && credentials.length === 0 && (
                  <span className="form-field__hint">
                    No provider credentials yet — add one in Fleet → Provider Credentials first.
                  </span>
                )}
              </label>

              {providerCredId.length > 0 && catalogPending && (
                <p className="form-field__hint" role="status">
                  Loading available regions and server types…
                </p>
              )}

              {providerCredId.length > 0 && catalogIsError && (
                <p className="form-error" role="alert">
                  Couldn't load the provider's pricing and availability. Try selecting the credential again.
                </p>
              )}

              {catalogLoaded && (
                <>
                  <label className="form-field" htmlFor={regionFieldId}>
                    <span>Region</span>
                    <select
                      id={regionFieldId}
                      value={provisionRegion}
                      onChange={(e) => setProvisionRegion(e.target.value)}
                      required
                    >
                      {catalogLocations.map((l) => (
                        <option key={l.name} value={l.name}>
                          {locationLabel(l)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="form-field" htmlFor={serverTypeFieldId}>
                    <span>Server type</span>
                    <select
                      id={serverTypeFieldId}
                      value={provisionServerType}
                      onChange={(e) => setProvisionServerType(e.target.value)}
                      required
                    >
                      {catalogServerTypes.map((s) => (
                        <option key={s.name} value={s.name}>
                          {serverTypeLabel(s, priceForLocation(s, provisionRegion))}
                        </option>
                      ))}
                    </select>
                    <span className="form-field__hint">
                      Billed by the provider directly — Comebye never marks this up.
                    </span>
                  </label>
                </>
              )}

              <div className="form-actions">
                <Button variant="secondary" onClick={() => setStep("target")}>Back</Button>
                <Button
                  variant="primary"
                  disabled={!canProvision}
                  onClick={handleProvisionSubmit}
                >
                  {createInstanceMutation.isPending ? "Provisioning…" : "Provision and continue"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {step === "laptop" && !canAdminister && (
        // Defense-in-depth (A-12): the entry points above are already
        // gated, but this step never mints/creates for a role the server
        // would refuse regardless of how "laptop" was reached.
        <div className="wizard-step">
          <p>Ask an admin to enrol a laptop.</p>
          <div className="form-actions">
            <Button variant="secondary" onClick={leaveLaptopStep}>Back</Button>
          </div>
        </div>
      )}

      {step === "laptop" && canAdminister && (
        <div className="wizard-step">
          {devEnvironments.length === 0 ? (
            <div className="wizard-empty">
              <p>Your laptop needs a development environment to join.</p>
              <p className="wizard-empty__hint">
                A local machine can never join production (ADR-0034) — create a non-production
                environment first.
              </p>

              {laptopEnvPaymentRequired && createEnvironmentMutation.error && (
                <Banner
                  tone="warning"
                  title="Licensed capacity reached"
                  className="add-host__payment-banner"
                  action={
                    canViewBilling && (
                      <Link href="/billing" className="banner__link">
                        View plan
                      </Link>
                    )
                  }
                >
                  {createEnvironmentMutation.error.message}
                </Banner>
              )}

              {createEnvironmentMutation.isError && !laptopEnvPaymentRequired && (
                <p className="form-error" role="alert">
                  {createEnvironmentMutation.error instanceof Error
                    ? createEnvironmentMutation.error.message
                    : "Couldn't create that environment."}
                </p>
              )}

              <div className="form-actions">
                <Button variant="secondary" onClick={leaveLaptopStep}>Back</Button>
                <Button
                  variant="primary"
                  disabled={createEnvironmentMutation.isPending}
                  onClick={() =>
                    createEnvironmentMutation.mutate({
                      name: "dev",
                      class: "development",
                      requiresPromotion: false,
                      allowedNodeTypes: [],
                      protected: false,
                    })
                  }
                >
                  {createEnvironmentMutation.isPending ? "Creating…" : "Create a dev environment"}
                </Button>
              </div>
            </div>
          ) : !devEnvironment ? (
            // More than one development-class environment exists — the
            // operator must name one explicitly (PR #634 security audit
            // condition 2); nothing mints until they do.
            <div className="wizard-empty">
              <p>Choose which development environment your laptop should join.</p>
              <label className="form-field" htmlFor={devEnvFieldId}>
                <span>Development environment</span>
                <select
                  id={devEnvFieldId}
                  value={selectedDevEnvName}
                  onChange={(e) => setSelectedDevEnvName(e.target.value)}
                  required
                >
                  <option value="" disabled>
                    Choose an environment…
                  </option>
                  {devEnvironments.map((env) => (
                    <option key={env.id} value={env.name}>
                      {env.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="form-actions">
                <Button variant="secondary" onClick={leaveLaptopStep}>Back</Button>
              </div>
            </div>
          ) : (
            <>
              <p className="wizard-step__intro">
                Run this on your laptop to bring it online in <strong>{devEnvironment.name}</strong>{" "}
                (needs Docker running and the <code>kelpie</code> binary on PATH).
              </p>

              {mintLaptopToken.isPending && <p className="add-host__status">Minting a join token…</p>}

              {mintLaptopToken.isError && (
                <div className="add-host__status add-host__status--error" role="alert">
                  <p>
                    {mintLaptopToken.error instanceof Error
                      ? mintLaptopToken.error.message
                      : "Couldn't mint a join token."}
                  </p>
                  <Button variant="secondary" onClick={retryLaptopMint}>
                    Try again
                  </Button>
                </div>
              )}

              {laptopToken && (
                <>
                  <div className="add-host__command">
                    <label className="add-host__label" htmlFor="wizard-laptop-command">
                      Run this
                    </label>
                    <code id="wizard-laptop-command" className="add-host__code">
                      {laptopCommand}
                    </code>
                    <CopyButton getValue={() => laptopCommand} label="Copy" />
                  </div>
                  <p className="add-host__note">
                    Assumes the control plane's tunnel listens on the default port 8444, the same
                    assumption the CLI's <code>comebye local</code> makes — pass a different address
                    yourself if yours doesn't.
                  </p>
                  <p className="wizard-step__intro" role="status">
                    <Spinner /> Waiting for your laptop to connect…
                  </p>
                </>
              )}

              <div className="form-actions">
                <Button variant="secondary" onClick={leaveLaptopStep}>Back</Button>
              </div>
            </>
          )}
        </div>
      )}

      {step === "hostname" && (
        <div className="wizard-step">
          {routedService ? (
            <>
              <p className="wizard-step__intro">
                <strong>{routedService.serviceName}</strong> listens on port{" "}
                {routedService.containerPort}. Comebye routes through bound hostnames rather than
                publishing compose's own ports — bind one so people (including you) can reach it.
              </p>

              {hostnameSkipped ? (
                <div className="wizard-empty">
                  <p>No hostname will be bound — you can add one later from the service page.</p>
                  <button type="button" className="wizard-text-btn" onClick={() => setHostnameSkipped(false)}>
                    Actually, bind one now
                  </button>
                </div>
              ) : (
                <>
                  <label className="form-field" htmlFor={hostnameId}>
                    <span>Hostname</span>
                    <input
                      id={hostnameId}
                      type="text"
                      autoComplete="off"
                      spellCheck={false}
                      value={hostname}
                      onChange={(e) => setHostname(e.target.value)}
                      placeholder="app.example.com"
                      maxLength={253}
                    />
                    {hostname.trim().length > 0 && !isValidHostname(hostname.trim()) && (
                      <span className="form-field__hint form-field__hint--error">
                        Use a real hostname — lowercase letters, digits, and hyphens, e.g.
                        app.example.com.
                      </span>
                    )}
                  </label>
                  <button
                    type="button"
                    className="wizard-text-btn"
                    onClick={() => {
                      setHostnameSkipped(true);
                      setHostname("");
                    }}
                  >
                    Skip — I'll bind a hostname later
                  </button>
                </>
              )}
            </>
          ) : (
            <div className="wizard-empty">
              <p>None of the imported services publish a port, so there's nothing to bind a hostname to yet.</p>
              <p className="wizard-empty__hint">Bind one later from the service page once you add one.</p>
            </div>
          )}

          <div className="form-actions">
            <Button variant="secondary" onClick={() => setStep("target")}>Back</Button>
            <Button variant="primary" disabled={!hostnameValid} onClick={goFromHostname}>
              {needsSecrets ? "Next: secrets" : "Next: review"}
            </Button>
          </div>
        </div>
      )}

      {step === "secrets" && (
        <div className="wizard-step">
          <p className="wizard-step__intro">
            This stack references {secrets.length} secret{secrets.length === 1 ? "" : "s"}.
            Enter the values — they're write-only and encrypted at rest.
          </p>

          {secrets.map((secret) => (
            <label key={secret.name} className="form-field">
              <span>{secret.name}</span>
              {secret.note && (
                <span className="form-field__hint">{secret.note}</span>
              )}
              <input
                type="password"
                autoComplete="off"
                value={secret.value}
                onChange={(e) => updateSecretValue(secret.name, e.target.value)}
              />
            </label>
          ))}

          <div className="form-actions">
            <Button variant="secondary" onClick={() => setStep("hostname")}>Back</Button>
            <Button variant="primary" disabled={!secretsValid} onClick={() => setStep("review")}>
              Next: review
            </Button>
          </div>
        </div>
      )}

      {step === "review" && (
        <div className="wizard-step">
          <dl className="wizard-summary">
            <div className="wizard-summary__row">
              <dt>Project</dt>
              <dd>{projectName}</dd>
            </div>
            <div className="wizard-summary__row">
              <dt>Stack</dt>
              <dd>
                {sourceMode === "recipe" && selectedRecipe
                  ? selectedRecipe.name
                  : "Custom compose"}
              </dd>
            </div>
            <div className="wizard-summary__row">
              <dt>Target</dt>
              <dd>
                {(() => {
                  const node = availableNodes.find((n) => n.id === selectedNodeId);
                  if (node) return nodeDisplayName(node);
                  if (provisioningHost?.state === "online") return provisioningHost.name || selectedNodeId;
                  return selectedNodeId;
                })()}
              </dd>
            </div>
            {routedService && (
              <div className="wizard-summary__row">
                <dt>URL</dt>
                <dd>
                  {!hostnameSkipped && hostname.trim().length > 0
                    ? urlForHostname(hostname.trim())
                    : "No URL (skipped)"}
                </dd>
              </div>
            )}
            {needsSecrets && (
              <div className="wizard-summary__row">
                <dt>Secrets</dt>
                <dd>
                  {secrets.length} secret{secrets.length === 1 ? "" : "s"} will
                  be created
                </dd>
              </div>
            )}
          </dl>

          <div className="form-actions">
            <Button
              variant="secondary"
              onClick={() => setStep(needsSecrets ? "secrets" : "hostname")}
            >
              Back
            </Button>
            <Button variant="primary" onClick={executeDeploy}>
              Deploy
            </Button>
          </div>
        </div>
      )}

      {step === "deploying" && (
        <div className="wizard-step">
          <div className="wizard-progress" role="log" aria-label="Deploy progress">
            {progressSnapshot.map((p, i) => (
              <div key={i} className="wizard-progress__item">
                <StatePill
                  label={p.error ? "Failed" : p.done ? "Done" : "Working"}
                  tone={p.error ? "negative" : p.done ? "positive" : "neutral"}
                  shape={p.error ? "triangle" : p.done ? "dot" : "ring"}
                />
                <span>
                  {p.label}{" "}
                  <span className="wizard-progress__detail">{p.detail}</span>
                </span>
              </div>
            ))}
          </div>

          {serviceStatuses.length > 0 && (
            <div className="wizard-services">
              <h3 className="wizard-services__heading">Services</h3>
              {serviceStatuses.map((svc) => (
                <div key={svc.serviceId} className="wizard-services__row">
                  <StatePill
                    label={serviceStatusLabel(svc.status)}
                    tone={svc.status === "done" ? "positive" : svc.status === "error" ? "negative" : "neutral"}
                    shape={svc.status === "done" ? "dot" : svc.status === "error" ? "triangle" : "ring"}
                  />
                  <span className="wizard-services__name">{svc.serviceName}</span>
                  {svc.error && (
                    <span className="wizard-services__error">{svc.error}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {deployError && (
            <p className="form-error" role="alert">{deployError}</p>
          )}

          {deployDone && doneUrl.kind === "live" && (
            <p className="wizard-done-url">
              <span className="wizard-done-url__caption">Your app is live at</span>{" "}
              <a href={doneUrl.url} target="_blank" rel="noreferrer">
                {doneUrl.url}
              </a>
            </p>
          )}
          {deployDone && doneUrl.kind === "routed-service-failed" && (
            <p className="wizard-done-url wizard-done-url--muted">
              {doneUrl.serviceName} failed to deploy, so its URL isn't reachable yet.
            </p>
          )}
          {deployDone && doneUrl.kind === "none" && (
            <p className="wizard-done-url wizard-done-url--muted">
              No URL yet — bind a hostname from the service page.
            </p>
          )}

          {deployDone && (
            <div className="form-actions">
              <Button variant="secondary" onClick={handleClose}>Close</Button>
              {createdProjectId && (
                <Button
                  variant="primary"
                  onClick={() => {
                    void queryClient.invalidateQueries({ queryKey: queryKeys.projects() });
                    void queryClient.invalidateQueries({ queryKey: queryKeys.nodes() });
                    handleClose();
                    navigate("/operate");
                  }}
                >
                  Go to Operate
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function serviceStatusLabel(status: ServiceDeployStatus["status"]): string {
  switch (status) {
    case "pending": return "Pending";
    case "placing": return "Placing";
    case "deploying": return "Deploying";
    case "done": return "Accepted";
    case "error": return "Failed";
  }
}
